import type { UserOffice } from '@artham/shared';

import * as zod from 'zod';
import { useState, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CreateUserProfileSchema } from '@artham/shared';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogContent from '@mui/material/DialogContent';

import { useTranslate } from 'src/locales';
import axiosInstance, { endpoints } from 'src/lib/axios-artham';

import { Iconify } from 'src/components/iconify';
import { Form, Field } from 'src/components/hook-form';
import { PhotoCaptureField } from 'src/components/upload';

import { ConsentFinePrint } from 'src/sections/artham/shared/consent-fine-print';

import { OfficeLocationField } from './office-location-field';

// ----------------------------------------------------------------------

// Only the name is genuinely required: it is the sole NOT NULL user-entered
// column on `users`. Everything else is nullable and can be completed later
// from the account drawer, so nothing here blocks a dealer from getting in.
const ProfileSchema = zod.object({
  fullName: zod.string().min(1, { message: 'Please enter your name' }),
  dob: zod.string().optional(),
  photoURL: zod.string().url().optional(),
  office: zod.custom<UserOffice>().optional(),
});

type ProfileSchemaType = zod.infer<typeof ProfileSchema>;

const TEXT_KEYS = ['address', 'area', 'city', 'state', 'country'] as const;

/**
 * Trims text and drops keys the dealer never touched, while preserving a key
 * they deliberately emptied - "" is how an update clears a column, so it has to
 * survive all the way to the API. Returns undefined when there is no office at
 * all, which is different from an office whose fields were cleared.
 */
function normalizeOffice(office: UserOffice | undefined): UserOffice | undefined {
  if (!office) return undefined;

  const out: UserOffice = {};

  for (const key of TEXT_KEYS) {
    const raw = office[key];
    if (raw !== undefined) out[key] = raw.trim();
  }
  if (office.pincode !== undefined) out.pincode = office.pincode.trim();
  if (office.photoURL !== undefined) out.photoURL = office.photoURL ?? '';
  if (office.location !== undefined) out.location = office.location;

  return Object.keys(out).length > 0 ? out : undefined;
}

export type UserProfileFormViewProps = {
  mode?: 'create' | 'update';
  currentProfile?: {
    fullName?: string;
    dob?: string;
    photoURL?: string;
    office?: UserOffice;
  };
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
  onClose?: () => void;
};

export function UserProfileFormView({
  mode = 'create',
  currentProfile,
  onSuccess,
  onError,
  onClose,
}: UserProfileFormViewProps) {
  const { t } = useTranslate('auth');

  const [errorMsg, setErrorMsg] = useState('');
  const [showMore, setShowMore] = useState(false);
  const [uploading, setUploading] = useState(0);

  const methods = useForm<ProfileSchemaType>({
    mode: 'onTouched',
    resolver: zodResolver(ProfileSchema),
    defaultValues: {
      fullName: currentProfile?.fullName ?? '',
      dob: currentProfile?.dob ?? '',
      photoURL: currentProfile?.photoURL,
      office: currentProfile?.office,
    },
  });

  const {
    watch,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const office = watch('office');

  // Ref-counted so two photos uploading at once cannot unlock the button early.
  const trackUpload = useCallback((busy: boolean) => {
    setUploading((n) => Math.max(0, n + (busy ? 1 : -1)));
  }, []);

  const onSubmit = handleSubmit(async (data) => {
    setErrorMsg('');

    const nextOffice = normalizeOffice(data.office);

    // Say so rather than quietly dropping it. Silently discarding what the
    // dealer typed is exactly what the old wizard did.
    // OfficeLocationField keeps its editor open while the pincode is invalid,
    // so the offending field is already on screen.
    if (nextOffice?.pincode && !/^\d{6}$/.test(nextOffice.pincode)) {
      setErrorMsg(t('officeLocation.pincodeInvalid'));
      return;
    }

    try {
      const payload = CreateUserProfileSchema.parse({
        fullName: data.fullName.trim(),
        dob: data.dob || undefined,
        photoURL: data.photoURL || undefined,
        office: nextOffice,
      });

      const response =
        mode === 'create'
          ? await axiosInstance.post(endpoints.auth.me, payload)
          : await axiosInstance.put(endpoints.auth.me, payload);

      // No consent calls from here. POST /auth/me captures TOS, PRIVACY and
      // DPDPA_PROCESSING server-side with the request's IP and user-agent, which
      // the client cannot supply anyway. Capturing again just returned 409
      // CONSENT_ALREADY_CAPTURED three times and logged each as a failed request.

      onSuccess?.(response.data);
    } catch (error: any) {
      console.error('Error submitting profile:', error);
      setErrorMsg(error?.error || error?.message || t('wizard.saveProfileError'));
      onError?.(error);
    }
  });

  const busy = isSubmitting || uploading > 0;

  return (
    // The dialog paper is a fixed-height flex column with overflow hidden, so
    // the form has to be the flex child and DialogContent the scroll surface.
    <Form
      methods={methods}
      onSubmit={onSubmit}
      sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
    >
      <DialogContent sx={{ px: { xs: 2.5, sm: 3 }, pb: 3, flex: 1, overflow: 'auto' }}>
        <Stack spacing={3} sx={{ maxWidth: 480, mx: 'auto', width: 1, pt: 1 }}>
          <Field.Text
            name="fullName"
            label={t('profileForm.fullName')}
            placeholder={t('profileForm.fullNamePlaceholder')}
            required
          />

          <Controller
            name="photoURL"
            control={methods.control}
            render={({ field }) => (
              <PhotoCaptureField
                shape="circle"
                frontCamera
                label={t('profileForm.yourPhoto')}
                helper={t('profileForm.yourPhotoHelper')}
                value={field.value}
                onChange={field.onChange}
                onBusyChange={trackUpload}
                disabled={isSubmitting}
              />
            )}
          />

          <Stack spacing={1}>
            <Typography variant="subtitle2">{t('profileForm.officeLocation')}</Typography>
            <OfficeLocationField
              value={office}
              onChange={(next) => setValue('office', next, { shouldDirty: true })}
            />
          </Stack>

          <Divider />

          {/* Everything below is optional. Collapsed by default so the form
              reads as three fields rather than ten. */}
          <Box>
            <ButtonBase
              onClick={() => setShowMore((prev) => !prev)}
              sx={{
                width: 1,
                py: 1,
                borderRadius: 1,
                justifyContent: 'space-between',
                typography: 'subtitle2',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Iconify
                  icon="eva:arrow-ios-forward-fill"
                  width={18}
                  sx={{
                    transition: (theme) => theme.transitions.create('transform'),
                    transform: showMore ? 'rotate(90deg)' : 'none',
                  }}
                />
                {t('profileForm.addMoreDetails')}
              </Stack>
              <Typography variant="caption" color="text.disabled">
                {t('common.optional')}
              </Typography>
            </ButtonBase>

            <Collapse in={showMore} unmountOnExit>
              <Stack spacing={3} sx={{ pt: 2 }}>
                {/* Year first — a birth year is decades back, so the default
                    day view means scrolling through hundreds of months. */}
                <Field.DatePicker
                  name="dob"
                  label={t('profileForm.dateOfBirth')}
                  openTo="year"
                  views={['year', 'month', 'day']}
                  disableFuture
                  slotProps={{ field: { clearable: false } }}
                />

                <PhotoCaptureField
                  shape="card"
                  label={t('profileForm.officePhoto')}
                  helper={t('profileForm.officePhotoHelper')}
                  value={office?.photoURL}
                  onChange={(url) =>
                    setValue(
                      'office',
                      { ...(office ?? {}), photoURL: url },
                      { shouldDirty: true }
                    )
                  }
                  onBusyChange={trackUpload}
                  disabled={isSubmitting}
                />
              </Stack>
            </Collapse>
          </Box>

          {errorMsg && <Alert severity="error">{errorMsg}</Alert>}

          <Stack spacing={1.5}>
            <ConsentFinePrint />

            <LoadingButton
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              loading={isSubmitting}
              disabled={uploading > 0}
            >
              {uploading > 0
                ? t('common.uploading')
                : mode === 'create'
                  ? t('profileForm.createProfile')
                  : t('profileForm.saveChanges')}
            </LoadingButton>

            {onClose && mode === 'update' && (
              <LoadingButton fullWidth color="inherit" onClick={onClose} disabled={busy}>
                {t('common.cancel')}
              </LoadingButton>
            )}
          </Stack>
        </Stack>
      </DialogContent>
    </Form>
  );
}
